import { useAppStore } from "@hooks/useAppStore";
import { useHotkeys } from "@memorang/helpers/hooks/useHotkeys";
import { isWeb } from "@memorang/helpers/platform";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useRef } from "react";

export function usePrinting() {
	const { print } = useLocalSearchParams<{ print?: string }>();
	const isPrintMode = print === "true";
	const appFont = useAppStore((state) => state.app.font);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);

	const handlePrint = useCallback(() => {
		if (isWeb) {
			router.setParams({ print: "true" });
		}
	}, []);

	const handleCancelPrint = useCallback(() => {
		router.setParams({ print: undefined });
	}, []);

	useHotkeys([["mod+p", () => handlePrint()]]);

	useEffect(() => {
		if (isPrintMode && typeof window !== "undefined") {
			const style = document.createElement("style");
			style.innerHTML = `
        @media print {
          @page { 
            size: portrait; 
            margin: 0; 
          }
          body { 
            font-family: ${appFont}; 
            margin: 1cm; 
          }
        }
      `;
			document.head.appendChild(style);

			const mediaQueryList = window.matchMedia("print");
			const handlePrintChange = (mql: MediaQueryListEvent) => {
				if (!mql.matches) {
					if (timeoutRef.current) {
						clearTimeout(timeoutRef.current);
					}
					timeoutRef.current = setTimeout(() => {
						handleCancelPrint();
					}, 300);
				}
			};

			mediaQueryList.addEventListener("change", handlePrintChange);

			return () => {
				mediaQueryList.removeEventListener("change", handlePrintChange);
				document.head.removeChild(style);
				window.addEventListener("afterprint", handleCancelPrint);
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
			};
		}
	}, [isPrintMode, handleCancelPrint, appFont]);

	return {
		isPrintMode,
		handlePrint,
		handleCancelPrint,
	};
}
